import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ShiftTypeItem } from './economy.service';

@Injectable({ providedIn: 'root' })
export class ShiftService {
    constructor(private readonly datatask: DataTaskService) {}

    /** @datatask 517 */
    public async getShift(params: ShiftParams): Promise<ShiftGet> { return (await this.datatask.Post(517, params))[0]; }
    /** @datatask 521 */
    public getWeekDays(params: ShiftParams): Promise<ShiftWeekDayItem[]> { return this.datatask.Post(521, params); }
    /** @datatask 518 */
    public async saveShift(page: ShiftGet): Promise<ShiftActionResponse> { return (await this.datatask.Post(518, page))[0]; }
    /** @datatask 519 */
    public async deleteShift(params: ShiftParams): Promise<ShiftActionResponse> { return (await this.datatask.Post(519, params))[0]; }
    /** @datatask 534 */
    public getDayTimes(params: ShiftDayParams): Promise<Item[]> { return this.datatask.Post(534, params); }
    /** @datatask 555 */
    public getDayBreaks(params: ShiftDayParams): Promise<Item[]> { return this.datatask.Post(555, params); }
    /** @datatask 535 */
    public async getDayTime(params: ShiftDayTimeParams): Promise<ShiftDayTimeGet> { return (await this.datatask.Post(535, params))[0]; }
    /** @datatask 538 */
    public async saveDayTime(page: ShiftDayTimeGet): Promise<void> { await this.datatask.Post(538, page); }
    /** @datatask 539 */
    public async deleteDayTime(params: Pick<ShiftDayTimeParams, 'shifttime_keyno'>): Promise<ShiftActionResponse> { return (await this.datatask.Post(539, params))[0]; }
    /** @datatask 556 */
    public async getDayBreak(params: ShiftDayBreakParams): Promise<ShiftDayBreakGet> { return (await this.datatask.Post(556, params))[0]; }
    /** @datatask 557 */
    public async saveDayBreak(page: ShiftDayBreakGet): Promise<ShiftActionResponse> { return (await this.datatask.Post(557, page))[0]; }
    /** @datatask 558 */
    public async deleteDayBreak(params: Pick<ShiftDayBreakParams, 'shiftbreak_keyno'>): Promise<ShiftActionResponse> { return (await this.datatask.Post(558, params))[0]; }
    /** @datatask 559 */
    public async validateBreakHours(page: ShiftDayBreakGet): Promise<Pick<ShiftDayBreakGet, 'breakhour'>> { return (await this.datatask.Post(559, page))[0]; }
    /** @datatask 2019 */
    public async getView(params: ShiftViewParams): Promise<ShiftViewGet> { return (await this.datatask.Post(2019, params))[0]; }
    /** @datatask 2031 */
    public getGraph(params: ShiftGraphParams): Promise<ShiftGraphItem[]> { return this.datatask.Post(2031, params); }
    /** @datatask 533 */
    public async saveCalendar(records: ShiftCalendarChange[]): Promise<ErrorMessageResponse> { return (await this.datatask.Post(533, { records }))[0]; }

    /** Loads dates and groups them into calendar months and weeks. @datatask 524 */
    public async getCalendar(params: ShiftCalendarParams, shifts: ShiftTypeItem[]): Promise<ShiftCalendar> {
        const dates: ShiftCalendarDay[] = await this.datatask.Post(524, params);
        const calendar: ShiftCalendar = { dates, months: {}, monthnames: {}, dayletters: {} };
        const shiftsById = new Map(shifts.map(shift => [shift.item_id, shift]));
        for (const day of dates) {
            day.original_background_color_hex = day.background_color_hex;
            day.original_text_color_hex = day.text_color_hex;
            const shift = shiftsById.get(day.shift_id);
            day.shift_background_color_hex = shift?.background_color_hex ?? day.background_color_hex;
            day.shift_text_color_hex = shift?.text_color_hex ?? day.text_color_hex;
            day.background_color_hex = day.shift_background_color_hex;
            day.text_color_hex = day.shift_text_color_hex;
            const month = String(day.month_no);
            const week = String(day.week_no);
            calendar.monthnames[month] = day.month_name.charAt(0).toUpperCase() + day.month_name.slice(1).toLowerCase();
            calendar.dayletters[String(day.week_day)] = day.day_letter;
            calendar.months[month] ??= {};
            calendar.months[month][week] ??= {};
            calendar.months[month][week][String(day.week_day)] = day;
        }
        for (const weeks of Object.values(calendar.months)) {
            for (const days of Object.values(weeks)) {
                const reference = Object.values(days)[0];
                for (let weekday = 1; weekday <= 7; weekday++) {
                    days[String(weekday)] ??= {
                        ...reference, systemdate: '', day_no: '', week_day: weekday, shift_id: '',
                        is_protected: '1', ischanged: '0', remark: '', time_from: '', time_to: '',
                        background_color_hex: '', text_color_hex: '', original_background_color_hex: '', original_text_color_hex: '',
                    };
                }
            }
        }
        return calendar;
    }
}

export interface ShiftParams { shift_id: string; }
export interface ShiftDayParams extends ShiftParams { dayno: string; }
export interface ShiftDayTimeParams extends ShiftDayParams { shifttime_keyno: string; }
export interface ShiftDayBreakParams extends ShiftDayParams { shiftbreak_keyno: string; }
export interface ShiftActionResponse { status_no: string | number; status_message: string; }
export interface ShiftGet extends ShiftParams {
    shift_name: string;
    in_proposal: '0' | '1';
    shift_allowance: '0' | '1';
    text_color_hex: string;
    background_color_hex: string;
    validok: string;
    is_new: '0' | '1';
}
export interface ShiftWeekDayItem extends Item { item_timeused: string; }
export interface ShiftDayTimeGet extends ShiftDayTimeParams {
    day_name: string;
    in_time: string;
    out_time: string;
    in_from_time: string;
    in_to_time: string;
    out_from_time: string;
    out_to_time: string;
    salarytype_id: string;
    core_rule: '0' | '1';
    include_in_capasity: '0' | '1';
}
export interface ShiftDayBreakGet extends ShiftDayBreakParams {
    day_name: string;
    from_time: string;
    to_time: string;
    breakhour: string | number;
}
export interface ShiftViewParams { labour_no: string; }
export interface ShiftCalendarParams extends ShiftViewParams { year: number; }
export interface ShiftGraphParams extends ShiftCalendarParams { bg_timeperiod: string; }
export interface ShiftViewGet { webpage_heading: string; view_id: string; p2_datatask_keyno: string | number; }
export interface ShiftGraphItem { item_name: string; quantity: string | number; }
export interface ShiftCalendarChange extends ShiftViewParams { systemdate: string; shift_id: string; }
export interface ShiftCalendarDay extends ShiftCalendarChange {
    year: string | number;
    week_no: string | number;
    month_no: string | number;
    month_name: string;
    day_no: string | number;
    week_day: string | number;
    day_letter: string;
    day_name: string;
    background_color_hex: string;
    text_color_hex: string;
    original_background_color_hex?: string;
    original_text_color_hex?: string;
    shift_background_color_hex?: string;
    shift_text_color_hex?: string;
    remark: string;
    time_from: string;
    time_to: string;
    is_protected: string | number;
    ischanged?: '0' | '1';
    style?: string;
}
export interface ShiftCalendar {
    dates: ShiftCalendarDay[];
    months: Record<string, Record<string, Record<string, ShiftCalendarDay>>>;
    monthnames: Record<string, string>;
    dayletters: Record<string, string>;
}
